<script setup lang="ts">
import { computed, onMounted, ref, Ref, watchEffect } from 'vue';
import FilterRange from './FilterRange.vue';
import type {
  FilterOption,
  Range,
  RangeBounds,
  SearchFilter,
  StepperValueLabelMapping,
} from '../../../search-panel-types';
import FilterChip from './FilterChip.vue';
import useLocale from '../../../composables/useLocale';
import FilterFieldLayout from './FilterFieldLayout.vue';
import { getRangeBounds, getRangeFilterValue, isOpenEndedRange } from '../../../content-filters';
import useRangeLabel from '../../../composables/use-range-label';

type Emits = {
  (e: 'filters-updated', filter: SearchFilter): void;
};
const emit = defineEmits<Emits>();

type Props = {
  items: FilterOption[];
  fieldKey: string;
  label: string;
  selected: SearchFilter;
  ranges: Range[] | [];
  valueLabelMappings?: StepperValueLabelMapping[];
};
const props = defineProps<Props>();
const { t } = useLocale();
const { formatRangeLabel } = useRangeLabel();

const valueToLabel = computed(
  () => new Map((props.valueLabelMappings ?? []).map((mapping) => [mapping.value, mapping.label])),
);

const getLabel = (value: number): number | string => valueToLabel.value.get(value) ?? value;

const selectedRangeOption = ref<FilterOption | undefined>();
const multiplesRanges = ref<Range[]>(props.ranges);
const currentRange: Ref<number[] | null> = ref(null);
const filterMax = ref<number>(0);

const findMaxValue = () => {
  filterMax.value =
    props.items.reduce(
      (prev: number | null, cur: FilterOption) => (Number(prev) > Number(cur.value) ? prev : Number(cur.value)),
      null,
    ) ?? 0;
};

const rangesData = computed(() =>
  multiplesRanges.value.map((multipleRange: Range) =>
    multipleRange.max === Number.MAX_SAFE_INTEGER ? { ...multipleRange, max: filterMax.value } : multipleRange,
  ),
);
const rangesDataFiltered = computed(() => rangesData.value.filter((r) => r.count > 0));

const getCount = () => {
  multiplesRanges.value.forEach((_, index) => {
    multiplesRanges.value[index].count = 0;
  });

  props.items.forEach(({ value, count }: FilterOption) => {
    const numValue = parseInt(value as string, 10);
    multiplesRanges.value.forEach((range, index) => {
      if (numValue >= range.min && numValue <= range.max) {
        multiplesRanges.value[index].count += count;
      }
    });
  });
};

onMounted(() => {
  getCount();
  findMaxValue();
});

watchEffect(() => {
  if (!props.selected[props.fieldKey]?.length) {
    selectedRangeOption.value = undefined;
    // Use the lowest available range min and the overall data max
    const lowestMin = rangesData.value.length > 0 ? rangesData.value[0].min : 0;
    currentRange.value = rangesDataFiltered.value.length > 0 ? [lowestMin, filterMax.value] : [];
    return;
  }
  [selectedRangeOption.value] = props.selected[props.fieldKey];

  // Show the selected range from the route, e.g. after a reload or back/forward
  const bounds = getRangeBounds(selectedRangeOption.value.value);
  // Only set it when it differs, a new array with the same values would make FilterRange emit the range again
  if (bounds && (currentRange.value?.[0] !== bounds.min || currentRange.value?.[1] !== bounds.max)) {
    currentRange.value = [bounds.min, bounds.max];
  }
});

const isExactRangeSelected = (filter: RangeBounds): boolean =>
  selectedRangeOption.value?.value === getRangeFilterValue(props.fieldKey, filter);

/**
 * Whether a chip is the selected range. An open-ended chip (e.g. '60+ min') only compares its min, since its max is
 * the data max, which can differ from the max stored in the route (after a reload or when other filters narrow it).
 */
const isRangeSelected = (chip: RangeBounds): boolean => {
  const selected = selectedRangeOption.value ? getRangeBounds(selectedRangeOption.value.value) : undefined;
  if (!selected || selected.min !== chip.min) {
    return false;
  }
  return isOpenEndedRange(props.ranges, chip) || selected.max === chip.max;
};

const applyRange = (filter: RangeBounds) => {
  if (isExactRangeSelected(filter)) {
    return;
  }
  const filterValue: SearchFilter = {
    [props.fieldKey]: [
      {
        value: getRangeFilterValue(props.fieldKey, filter),
        label: formatRangeLabel(props.fieldKey, filter, props.valueLabelMappings, filter.max === filterMax.value),
        count: 1,
      },
    ],
  };

  emit('filters-updated', filterValue);
  currentRange.value = [filter.min, filter.max];
};

const removeSelectedFilter = () => {
  emit('filters-updated', {
    [props.fieldKey]: [],
  });
  currentRange.value = [];
};

const toggleChip = (chip: RangeBounds) => (isRangeSelected(chip) ? removeSelectedFilter() : applyRange(chip));

const durationLabel = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  return minutes >= 1 ? `${minutes}m` : `${seconds}s`;
};
</script>

<template>
  <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
    <template v-if="props.items?.length > 0" #content>
      <FilterRange
        :items="props.items"
        :field-key="props.fieldKey"
        :label="props.label"
        :selected="props.selected"
        :multiple="true"
        :custom-range="currentRange"
        :value-label-mappings="props.valueLabelMappings"
        custom-label
        class="pr-5 pb-0 pt-8 pb-2"
        @range-updated="applyRange"
      >
        <template #default="rangeSlotProps">
          <p v-if="fieldKey === 'duration'">{{ durationLabel(rangeSlotProps.value) }}</p>
          <p v-else>{{ getLabel(rangeSlotProps.value) }}</p>
        </template>
      </FilterRange>

      <div v-if="fieldKey === 'duration'" class="d-flex flex-wrap gap-between-chips pa-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min / 60 : chip.min / 60 + '-'}${
              chip.max === filterMax ? '+' : chip.max / 60
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="chip.count"
            :is-selected="isRangeSelected(chip)"
            @click="toggleChip(chip)"
          />
        </div>
      </div>
      <div
        v-else-if="fieldKey === 'properties.readabilityMetrics.iltLevel'"
        class="d-flex flex-wrap gap-between-chips pa-2"
      >
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.min"
            :label="`${t('filter.level')} ${chip.min}`"
            :count="chip.count"
            :is-selected="isRangeSelected(chip)"
            @click="toggleChip(chip)"
          />
        </div>
      </div>
      <div v-else-if="fieldKey === 'properties.decodabilityLevel'" class="d-flex flex-wrap gap-between-chips pa-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.min"
            :label="`${t('filter.level')} ${getLabel(chip.min)}`"
            :count="chip.count"
            :is-selected="isRangeSelected(chip)"
            @click="toggleChip(chip)"
          />
        </div>
      </div>
      <div v-else-if="fieldKey === 'ages'" class="d-flex flex-wrap gap-between-chips pa-2">
        <div v-for="chip in rangesData" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min : chip.min + '-'}${
              chip.max === filterMax ? '+' : chip.max
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="''"
            :is-selected="isRangeSelected(chip)"
            @click="toggleChip(chip)"
          />
        </div>
      </div>
      <div v-else class="d-flex flex-wrap gap-between-chips pa-2">
        <div v-for="chip in rangesDataFiltered" :key="chip.min">
          <FilterChip
            class="mb-0"
            :key="chip.max"
            :label="`${chip.max === filterMax ? chip.min : chip.min + '-'}${
              chip.max === filterMax ? '+' : chip.max
            } ${t(`filter.${props.fieldKey}`)}`"
            :count="fieldKey === 'pages' ? chip.count : ''"
            :is-selected="isRangeSelected(chip)"
            @click="toggleChip(chip)"
          />
        </div>
      </div>
    </template>
  </FilterFieldLayout>
</template>

<style scoped lang="scss">
.gap-between-chips {
  gap: 14px;
}
</style>
